import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { name: 'Services', href: '/#services', isHash: true },
  { name: 'Windscreen Calibration', href: '/windscreen-calibration-adelaide', isHash: false },
  { name: 'Why Us', href: '/#why-us', isHash: true },
  { name: 'Process', href: '/#process', isHash: true },
  { name: 'Industries', href: '/#industries', isHash: true },
  { name: 'FAQ', href: '/#faq', isHash: true },
  { name: 'Contact', href: '/#contact', isHash: true },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, link) => {
    setIsMobileMenuOpen(false);
    
    if (link.isHash) {
      // If we're on the homepage, scroll to section
      if (location.pathname === '/') {
        e.preventDefault();
        const sectionId = link.href.replace('/#', '#');
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      // If not on homepage, the link will navigate to /#section
    }
    // For non-hash links, let the default navigation happen
  };

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = '/#contact';
    }
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== '/' ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/"
            onClick={handleLogoClick}
            className="flex items-center"
            data-testid="header-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_b16acfe5-1235-44c1-ace5-482b8c36602b/artifacts/14ftomu2_Advanced-ADAS-w-logo-white-with-red-768x158.png"
              alt="Advanced ADAS Calibrations"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.isHash ? (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="text-slate-300 hover:text-white text-sm font-medium transition-colors"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.href 
                      ? 'text-blue-400' 
                      : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:0402454588" 
              className="flex items-center gap-2 text-slate-300 hover:text-white text-sm"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>0402 454 588</span>
            </a>
            <Button
              onClick={handleContactClick}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-blue-500/20"
              data-testid="header-cta-btn"
            >
              Request Calibration
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden mobile-menu fixed inset-0 top-16 z-40"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              link.isHash ? (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="text-white text-lg font-medium py-2 border-b border-slate-700"
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-lg font-medium py-2 border-b border-slate-700 ${
                    location.pathname === link.href 
                      ? 'text-blue-400' 
                      : 'text-white'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <a 
              href="tel:0402454588" 
              className="flex items-center gap-2 text-blue-400 text-lg mt-4"
            >
              <Phone className="w-5 h-5" />
              <span>0402 454 588</span>
            </a>
            <Button
              onClick={handleContactClick}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full mt-4"
              data-testid="mobile-cta-btn"
            >
              Request Calibration
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
